'use client'

import { NudgeButton } from './NudgeButton'

// Custom list-table cells for Z2H Applications — interview observability
// rendered straight from rowData the list view already fetched (no extra
// queries). Registered on `ui` fields in the collection config.

const DIMENSIONS = {
  founder_team: 'Founder & team',
  problem_market: 'Problem & market',
  traction_validation: 'Traction',
  business_model: 'Business model',
  financial_readiness: 'Financials',
  tech_feasibility: 'Tech feasibility',
  capital_fit: 'Capital fit',
  commitment: 'Commitment',
}
const STALE_MS = 48 * 60 * 60 * 1000
const OPEN_STATUSES = ['in-progress', 'needs-info']

function coveredDimensions(rowData) {
  const answers =
    rowData?.answers && typeof rowData.answers === 'object' ? rowData.answers : {}
  return Object.keys(DIMENSIONS).filter(
    (d) => answers[d] && Object.keys(answers[d]).length > 0,
  )
}

function lastActivityIso(rowData) {
  const transcript = Array.isArray(rowData?.transcript) ? rowData.transcript : []
  return transcript[transcript.length - 1]?.at || rowData?.updatedAt || null
}

// Scorecard dimensions with extracted evidence — ●●○○○○○○ 2/8.
export function CoverageCell({ rowData }) {
  const covered = coveredDimensions(rowData)
  const names = covered.map((d) => DIMENSIONS[d]).join(', ')
  return (
    <span
      title={names || 'No evidence extracted yet'}
      style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
    >
      <span aria-hidden style={{ letterSpacing: 2, marginRight: 6 }}>
        {Object.keys(DIMENSIONS)
          .map((d) => (covered.includes(d) ? '●' : '○'))
          .join('')}
      </span>
      {covered.length}/8
    </span>
  )
}

// Founder replies so far — a feel for how deep the interview went.
export function TurnsCell({ rowData }) {
  const transcript = Array.isArray(rowData?.transcript) ? rowData.transcript : []
  const founderTurns = transcript.filter((t) => t.role === 'founder').length
  return (
    <span title={`${transcript.length} total messages`} style={{ fontVariantNumeric: 'tabular-nums' }}>
      {founderTurns}
    </span>
  )
}

// Time since the last interview message; open applications quiet for 2+ days
// flag as stale — those are the follow-up candidates.
export function ActivityCell({ rowData }) {
  const iso = lastActivityIso(rowData)
  if (!iso) return <span>—</span>
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  const label =
    mins < 60
      ? `${mins}m ago`
      : mins < 60 * 48
        ? `${Math.round(mins / 60)}h ago`
        : `${Math.round(mins / 1440)}d ago`
  const stale =
    OPEN_STATUSES.includes(rowData?.status) &&
    Date.now() - new Date(iso).getTime() > STALE_MS
  return (
    <span
      title={new Date(iso).toLocaleString()}
      style={{
        whiteSpace: 'nowrap',
        color: stale ? 'var(--theme-warning-500, #b45309)' : undefined,
      }}
    >
      {label}
      {stale ? ' · stale' : ''}
    </span>
  )
}

// One-click follow-up for open, reachable applications.
export function NudgeCell({ rowData }) {
  if (!OPEN_STATUSES.includes(rowData?.status)) {
    return <span style={{ color: 'var(--theme-elevation-400)' }}>—</span>
  }
  if (!rowData?.email || !rowData?.inviteToken) {
    return (
      <span
        title="No email or resume token on this application — it cannot be nudged."
        style={{ color: 'var(--theme-elevation-400)', fontSize: 12 }}
      >
        unreachable
      </span>
    )
  }
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <NudgeButton applicationId={rowData.id} lastNudgedAt={rowData.lastNudgedAt || null} />
    </span>
  )
}

// ui fields require a Field component; these columns are list-only.
export function NoField() {
  return null
}
